import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

export interface ReportSection {
  id: string;
  title: string;
  parentId?: string;
  isChild?: boolean;
  // 'block' = a colored sub-block discovered from the rendered DOM. Block
  // visibility is controlled via `removedBlocks` (DOM overlay), NOT via
  // `selectedSections`, so toggling a block never re-renders the report tree.
  kind?: 'section' | 'block';
}

export interface SectionPosition {
  x: number;           // X from left edge of content area
  y: number;           // Y from top of content area (within page)
  pageNumber: number;  // Which page section is on
}

export interface PageSettings {
  size: 'a4' | 'letter';
  orientation: 'portrait' | 'landscape';
  margins: number; // in mm
}

export interface PdfExportState {
  selectedSections: Record<string, boolean>;
  sectionOrder: string[];
  manualPageBreaks: Set<string>; // Break AFTER these section IDs
  keepTogether: Set<string>; // These sections should not split across pages
  pageSettings: PageSettings;
  zoom: number;
}

// A4 dimensions at 96 DPI
export const PAGE_DIMENSIONS = {
  a4: {
    portrait: { width: 794, height: 1123 },
    landscape: { width: 1123, height: 794 }
  },
  letter: {
    portrait: { width: 816, height: 1056 },
    landscape: { width: 1056, height: 816 }
  }
};

// Convert mm to pixels at 96 DPI (1 inch = 25.4mm, 96 DPI)
export const mmToPixels = (mm: number): number => Math.round((mm / 25.4) * 96);

export const DEFAULT_PAGE_SETTINGS: PageSettings = {
  size: 'a4',
  orientation: 'portrait',
  margins: 15 // 15mm margins
};

export const DEFAULT_ZOOM = 0.75; // 75% zoom for better overview

interface UsePdfExportStateProps {
  initialSections: ReportSection[];
  storageKey?: string;
}

interface UsePdfExportStateReturn {
  // State
  selectedSections: Record<string, boolean>;
  sectionOrder: string[];
  manualPageBreaks: Set<string>;
  keepTogether: Set<string>;
  pageSettings: PageSettings;
  zoom: number;
  sectionPositions: Map<string, SectionPosition>; // Free positioning
  removedBlocks: Set<string>; // Colored sub-blocks hidden via DOM overlay
  blockRevision: number; // Bumped on block toggle to force re-clone/re-measure

  // Computed
  orderedSections: ReportSection[];
  pageDimensions: { width: number; height: number };
  contentDimensions: { width: number; height: number };
  marginPixels: number;

  // Actions
  toggleSection: (sectionId: string) => void;
  toggleAllSections: (selected: boolean) => void;
  reorderSections: (fromIndex: number, toIndex: number) => void;
  togglePageBreak: (sectionId: string) => void;
  toggleKeepTogether: (sectionId: string) => void;
  toggleBlock: (blockId: string) => void;
  setPageSettings: (settings: Partial<PageSettings>) => void;
  setZoom: (zoom: number) => void;
  resetToDefaults: () => void;
  // Position actions
  updateSectionPosition: (sectionId: string, position: Partial<SectionPosition>) => void;
  initializeSectionPosition: (sectionId: string, pageNumber: number, y: number) => void;
}

export function usePdfExportState({
  initialSections,
  storageKey
}: UsePdfExportStateProps): UsePdfExportStateReturn {
  // Initialize section order from initial sections
  const initialOrder = useMemo(
    () => initialSections.map(s => s.id),
    [initialSections]
  );

  // Initialize all sections as selected
  const initialSelection = useMemo(() => {
    const selection: Record<string, boolean> = {};
    initialSections.forEach(s => {
      selection[s.id] = true;
    });
    return selection;
  }, [initialSections]);

  // State
  const [selectedSections, setSelectedSections] = useState<Record<string, boolean>>(initialSelection);
  const [sectionOrder, setSectionOrder] = useState<string[]>(initialOrder);
  const [manualPageBreaks, setManualPageBreaks] = useState<Set<string>>(new Set());
  const [keepTogether, setKeepTogether] = useState<Set<string>>(new Set());
  const [pageSettings, setPageSettingsState] = useState<PageSettings>(DEFAULT_PAGE_SETTINGS);
  const [zoom, setZoomState] = useState<number>(DEFAULT_ZOOM);
  const [sectionPositions, setSectionPositions] = useState<Map<string, SectionPosition>>(new Map());
  const [removedBlocks, setRemovedBlocks] = useState<Set<string>>(new Set());
  const [blockRevision, setBlockRevision] = useState<number>(0);

  // Track if we've initialized with real sections
  const hasInitialized = useRef(false);

  // Sync state when initialSections changes (e.g., loaded asynchronously)
  useEffect(() => {
    // Only update if we have sections and haven't initialized yet, OR if sections changed
    if (initialSections.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;

      // Initialize selection - all sections selected by default
      const newSelection: Record<string, boolean> = {};
      initialSections.forEach(s => {
        newSelection[s.id] = true;
      });
      setSelectedSections(newSelection);

      // Initialize order
      setSectionOrder(initialSections.map(s => s.id));
    }
  }, [initialSections]);

  // Computed: ordered sections based on current order
  const orderedSections = useMemo(() => {
    const sectionMap = new Map(initialSections.map(s => [s.id, s]));
    return sectionOrder
      .map(id => sectionMap.get(id))
      .filter((s): s is ReportSection => s !== undefined);
  }, [initialSections, sectionOrder]);

  // Computed: page dimensions based on settings
  const pageDimensions = useMemo(() => {
    return PAGE_DIMENSIONS[pageSettings.size][pageSettings.orientation];
  }, [pageSettings.size, pageSettings.orientation]);

  // Computed: margin in pixels
  const marginPixels = useMemo(() => {
    return mmToPixels(pageSettings.margins);
  }, [pageSettings.margins]);

  // Computed: content area dimensions (page minus margins)
  const contentDimensions = useMemo(() => {
    return {
      width: pageDimensions.width - (marginPixels * 2),
      height: pageDimensions.height - (marginPixels * 2)
    };
  }, [pageDimensions, marginPixels]);

  // Action: toggle a single section
  const toggleSection = useCallback((sectionId: string) => {
    setSelectedSections(prev => {
      const updated = { ...prev };
      const newValue = !prev[sectionId];
      updated[sectionId] = newValue;

      // If toggling a parent section, toggle all children too
      const section = initialSections.find(s => s.id === sectionId);
      if (section && !section.isChild) {
        initialSections
          .filter(s => s.parentId === sectionId)
          .forEach(child => {
            updated[child.id] = newValue;
          });
      }

      return updated;
    });
  }, [initialSections]);

  // Action: toggle all sections
  const toggleAllSections = useCallback((selected: boolean) => {
    setSelectedSections(prev => {
      const updated: Record<string, boolean> = {};
      Object.keys(prev).forEach(id => {
        updated[id] = selected;
      });
      return updated;
    });
  }, []);

  // Action: reorder sections (from drag-drop)
  const reorderSections = useCallback((fromIndex: number, toIndex: number) => {
    setSectionOrder(prev => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  }, []);

  // Action: toggle manual page break after section
  const togglePageBreak = useCallback((sectionId: string) => {
    setManualPageBreaks(prev => {
      const updated = new Set(prev);
      if (updated.has(sectionId)) {
        updated.delete(sectionId);
      } else {
        updated.add(sectionId);
      }
      return updated;
    });
  }, []);

  // Action: toggle keep-together for section
  const toggleKeepTogether = useCallback((sectionId: string) => {
    setKeepTogether(prev => {
      const updated = new Set(prev);
      if (updated.has(sectionId)) {
        updated.delete(sectionId);
      } else {
        updated.add(sectionId);
      }
      return updated;
    });
  }, []);

  // Action: toggle removal of a colored sub-block (DOM overlay).
  // Kept separate from selectedSections so toggling a block does NOT change the
  // renderer key in PdfExportPreview (no re-render → inline edits survive).
  const toggleBlock = useCallback((blockId: string) => {
    setRemovedBlocks(prev => {
      const updated = new Set(prev);
      if (updated.has(blockId)) {
        updated.delete(blockId);
      } else {
        updated.add(blockId);
      }
      return updated;
    });
    // Force the WYSIWYG clones to re-read innerHTML and pagination to recompute.
    setBlockRevision(r => r + 1);
  }, []);

  // Action: update page settings
  const setPageSettings = useCallback((settings: Partial<PageSettings>) => {
    setPageSettingsState(prev => ({ ...prev, ...settings }));
  }, []);

  // Action: set zoom level
  const setZoom = useCallback((newZoom: number) => {
    // Clamp zoom between 0.25 and 1.5
    const clamped = Math.max(0.25, Math.min(1.5, newZoom));
    setZoomState(clamped);
  }, []);

  // Action: update section position (for drag-and-drop)
  const updateSectionPosition = useCallback((sectionId: string, position: Partial<SectionPosition>) => {
    setSectionPositions(prev => {
      const updated = new Map(prev);
      const existing = updated.get(sectionId);
      if (existing) {
        updated.set(sectionId, { ...existing, ...position });
      } else {
        // Create new position if doesn't exist
        updated.set(sectionId, {
          x: position.x ?? 0,
          y: position.y ?? 0,
          pageNumber: position.pageNumber ?? 1
        });
      }
      return updated;
    });
  }, []);

  // Action: initialize section position
  const initializeSectionPosition = useCallback((sectionId: string, pageNumber: number, y: number) => {
    setSectionPositions(prev => {
      if (prev.has(sectionId)) return prev; // Don't override existing
      const updated = new Map(prev);
      updated.set(sectionId, {
        x: 0,
        y,
        pageNumber
      });
      return updated;
    });
  }, []);

  // Action: reset to defaults
  const resetToDefaults = useCallback(() => {
    // Re-initialize selection - all sections selected
    const newSelection: Record<string, boolean> = {};
    initialSections.forEach(s => {
      newSelection[s.id] = true;
    });
    setSelectedSections(newSelection);
    setSectionOrder(initialSections.map(s => s.id));
    setManualPageBreaks(new Set());
    setKeepTogether(new Set());
    setPageSettingsState(DEFAULT_PAGE_SETTINGS);
    setZoomState(DEFAULT_ZOOM);
    setSectionPositions(new Map()); // Clear positions
    setRemovedBlocks(new Set()); // Restore all colored sub-blocks
    setBlockRevision(r => r + 1);
  }, [initialSections]);

  return {
    // State
    selectedSections,
    sectionOrder,
    manualPageBreaks,
    keepTogether,
    pageSettings,
    zoom,
    sectionPositions,
    removedBlocks,
    blockRevision,

    // Computed
    orderedSections,
    pageDimensions,
    contentDimensions,
    marginPixels,

    // Actions
    toggleSection,
    toggleAllSections,
    reorderSections,
    togglePageBreak,
    toggleKeepTogether,
    toggleBlock,
    setPageSettings,
    setZoom,
    resetToDefaults,
    // Position actions
    updateSectionPosition,
    initializeSectionPosition
  };
}

export default usePdfExportState;
